import React from "react";
import {
  CalendarOff, Scale, CalendarDays, Sun, Banknote, Plus,
} from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { PageShell } from "../../components/PageShell";
import { SectionCard, Button, Badge, Avatar } from "../../components/primitives";
import { DataTable, EmptyState } from "../../components/DataTable";
import { Input, Field, Select } from "../../components/forms";
import { Grid, Stack, Row, Hint, StatusBadge, MiniMonth, Legend } from "./kit";
import { useHrms, useFetch, fmtDate, fmtDateShort, fullName, monthKey } from "./useHrms";

// HRMS ▸ Leave — the approval queue, org balances, calendar and holidays.
// Employee self-service (apply/cancel) lives in My HR ▸ Leave.

const TABS = [
  { key: "requests", label: "Requests", icon: <CalendarOff size={14} /> },
  { key: "balances", label: "Balances", icon: <Scale size={14} /> },
  { key: "calendar", label: "Calendar", icon: <CalendarDays size={14} /> },
  { key: "holidays", label: "Holidays", icon: <Sun size={14} /> },
  { key: "encashment", label: "Encashment", icon: <Banknote size={14} /> },
];

export default function HrmsLeave() {
  const t = useDashTheme();
  const [tab, setTab] = React.useState("requests");

  return (
    <PageShell
      eyebrow="HRMS"
      title="Leave"
      subtitle="Requests, balances and holidays — the ledger no one argues with"
      icon={<CalendarOff size={16} />}
      tabs={TABS}
      activeTab={tab}
      onTabChange={setTab}
    >
      {tab === "requests" && <RequestsTab t={t} />}
      {tab === "balances" && <BalancesTab t={t} />}
      {tab === "calendar" && <CalendarTab t={t} />}
      {tab === "holidays" && <HolidaysTab t={t} />}
      {tab === "encashment" && <EncashmentTab t={t} />}
    </PageShell>
  );
}

function RequestsTab({ t }) {
  const { post } = useHrms();
  const inbox = useFetch("/approvals/inbox?type=leave");
  const all = useFetch("/leave/requests");

  const decide = async (id, action) => {
    await post(`/approvals/${id}/decide`, { action });
    inbox.refresh(); all.refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      {(inbox.data || []).length > 0 && (
        <SectionCard title="Waiting on you" padding={0} actions={<Badge tone="warning">{inbox.data.length}</Badge>}>
          <DataTable
            columns={[
              { key: "who", header: "Employee", render: (r) => (
                <Row gap={10}>
                  <Avatar name={fullName(r.employeeId)} src={r.employeeId?.avatar} size={24} />
                  <span>{fullName(r.employeeId)}</span>
                </Row>
              ) },
              { key: "summary", header: "Request", render: (r) => r.summary },
              { key: "when", header: "Raised", render: (r) => fmtDate(r.createdAt) },
              {
                key: "act", header: "", align: "right",
                render: (r) => (
                  <Row gap={4} justify="flex-end">
                    <Button size="sm" variant="primary" onClick={() => decide(r._id, "approve")}>Approve</Button>
                    <Button size="sm" variant="ghost" onClick={() => decide(r._id, "reject")}>Reject</Button>
                  </Row>
                ),
              },
            ]}
            rows={inbox.data}
          />
        </SectionCard>
      )}

      <SectionCard title="All requests" subtitle="Scope: yours, your team's, or the whole org for HR" padding={0}>
        <DataTable
          loading={all.loading}
          columns={[
            { key: "who", header: "Employee", render: (r) => fullName(r.employeeId) },
            { key: "type", header: "Type", render: (r) => r.leaveTypeId?.name },
            { key: "dates", header: "Dates", render: (r) => `${fmtDateShort(r.fromDate)} → ${fmtDateShort(r.toDate)}` },
            { key: "days", header: "Days", align: "right", render: (r) => r.days },
            { key: "reason", header: "Reason", render: (r) => r.reason || "—" },
            { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(r.status)} /> },
          ]}
          rows={all.data || []}
          emptyState={<EmptyState title="No leave requests" description="Employees apply from My HR ▸ Leave." />}
        />
      </SectionCard>
    </Stack>
  );
}

function BalancesTab({ t }) {
  const { isHr } = useHrms();
  const year = new Date().getFullYear();
  const mine = useFetch("/leave/balances");
  const all = useFetch(isHr ? `/leave/balances/all?year=${year}` : null);

  if (!isHr) {
    return (
      <Grid min={220}>
        {(mine.data || []).map((b) => (
          <SectionCard key={b._id} title={b.leaveTypeId?.name} padding={16}>
            <Row justify="space-between">
              <span style={{ fontSize: 22, fontWeight: 600, color: t.c.text }}>{b.available}</span>
              <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>
                used {b.consumed} · pending {b.pending}
              </span>
            </Row>
          </SectionCard>
        ))}
      </Grid>
    );
  }

  // HR: pivot rows into one line per employee.
  const byEmp = new Map();
  for (const b of all.data || []) {
    const k = b.employeeId?._id;
    if (!k) continue;
    const cur = byEmp.get(k) || { employee: b.employeeId, types: {} };
    cur.types[b.leaveTypeId?.name] = b.available;
    byEmp.set(k, cur);
  }
  const typeNames = [...new Set((all.data || []).map((b) => b.leaveTypeId?.name).filter(Boolean))];

  return (
    <SectionCard title={`Balances · ${year}`} subtitle="Available days per person and type" padding={0}>
      <DataTable
        loading={all.loading}
        columns={[
          { key: "who", header: "Employee", render: (r) => fullName(r.employee) },
          ...typeNames.map((name) => ({
            key: name, header: name, align: "right",
            render: (r) => r.types[name] ?? "—",
          })),
        ]}
        rows={[...byEmp.values()]}
        emptyState={<EmptyState title="No balances yet" description="Balances initialize when employees first touch leave." />}
      />
    </SectionCard>
  );
}

function CalendarTab({ t }) {
  const [month, setMonth] = React.useState(monthKey());
  const { data } = useFetch(`/leave/calendar?month=${month}`);
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7)) - 1;

  const marks = {};
  for (const h of data?.holidays || []) marks[new Date(h.date).toISOString().slice(0, 10)] = { tone: "holiday", label: h.name };
  for (const l of data?.leaves || []) {
    for (let d = new Date(l.fromDate); d <= new Date(l.toDate); d = new Date(d.getTime() + 86400000)) {
      const k = d.toISOString().slice(0, 10);
      if (k.startsWith(month)) marks[k] = { tone: "leave", label: `${fullName(l.employeeId)} · ${l.leaveTypeId?.name}` };
    }
  }
  const shift = (delta) => setMonth(new Date(Date.UTC(y, m + delta, 1)).toISOString().slice(0, 7));

  return (
    <Grid min={320}>
      <SectionCard
        title={new Date(y, m).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
        subtitle="Who is out, when"
        actions={<Row gap={4}>
          <Button size="sm" variant="ghost" onClick={() => shift(-1)}>‹</Button>
          <Button size="sm" variant="ghost" onClick={() => shift(1)}>›</Button>
        </Row>}
      >
        <MiniMonth year={y} month={m} marks={marks} />
        <div style={{ marginTop: t.s[4], paddingTop: t.s[3], borderTop: `1px solid ${t.c.border}` }}>
          <Legend items={[
            { label: "Holiday", color: t.status.info },
            { label: "On leave", color: t.status.warning },
          ]} />
        </div>
      </SectionCard>

      <SectionCard title="Out this month" padding={0}>
        <DataTable
          columns={[
            { key: "who", header: "Employee", render: (r) => fullName(r.employeeId) },
            { key: "type", header: "Type", render: (r) => r.leaveTypeId?.name },
            { key: "dates", header: "Dates", render: (r) => `${fmtDateShort(r.fromDate)} → ${fmtDateShort(r.toDate)}` },
            { key: "days", header: "Days", align: "right", render: (r) => r.days },
          ]}
          rows={data?.leaves || []}
          emptyState={<EmptyState title="Nobody out" description="No approved leave this month." />}
        />
      </SectionCard>
    </Grid>
  );
}

function HolidaysTab({ t }) {
  const { isHr, post } = useHrms();
  const cals = useFetch("/holiday-calendars");
  const { data, loading, refresh } = useFetch("/holidays");
  const [form, setForm] = React.useState({ date: "", name: "", type: "national" });

  const add = async () => {
    if (!form.date || !form.name.trim() || !cals.data?.length) return;
    await post("/holidays", { ...form, calendarId: cals.data[0]._id });
    setForm({ date: "", name: "", type: "national" });
    refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      <SectionCard title="Holidays" subtitle={cals.data?.[0]?.name ? `Calendar: ${cals.data[0].name}` : undefined} padding={0}>
        <DataTable
          loading={loading}
          columns={[
            { key: "date", header: "Date", render: (r) => fmtDate(r.date) },
            { key: "name", header: "Holiday" },
            { key: "type", header: "Type", render: (r) => <Badge tone={r.type === "optional" ? "neutral" : "info"}>{r.type}</Badge> },
          ]}
          rows={(data || []).slice().sort((a, b) => new Date(a.date) - new Date(b.date))}
          emptyState={<EmptyState title="No holidays" description="Add the year's holiday list below." />}
        />
      </SectionCard>

      {isHr && (
        <SectionCard title="Add holiday" padding={16}>
          <Row gap={12} wrap align="flex-end">
            <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} /></Field>
            <Field label="Name"><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Diwali" /></Field>
            <Field label="Type">
              <Select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                {["national", "regional", "optional"].map((ty) => <option key={ty} value={ty}>{ty}</option>)}
              </Select>
            </Field>
            <Button variant="primary" leftIcon={<Plus size={13} />} onClick={add}>Add</Button>
          </Row>
        </SectionCard>
      )}
    </Stack>
  );
}

function EncashmentTab({ t }) {
  const { isHr, post } = useHrms();
  const year = new Date().getFullYear();
  const all = useFetch(isHr ? `/leave/balances/all?year=${year}` : null);
  const types = useFetch("/leave/types");
  const [form, setForm] = React.useState({ employeeId: "", leaveTypeId: "", delta: "", reason: "Encashment" });
  const [done, setDone] = React.useState(null);

  if (!isHr) {
    return <EmptyState title="HR only" description="Encashment and balance adjustments are managed by HR. Your encashable balance shows in Balances." />;
  }

  const encashable = (types.data || []).filter((ty) => ty.encashable);
  const employees = [...new Map((all.data || []).map((b) => [b.employeeId?._id, b.employeeId])).values()].filter(Boolean);

  const adjust = async () => {
    if (!form.employeeId || !form.leaveTypeId || !form.delta) return;
    const r = await post("/leave/balances/adjust", { ...form, delta: Number(form.delta), year });
    setDone(r?.payout
      ? `Adjusted ${form.delta} day(s) · payout of ${r.payout.amount} queued for the next payroll run.`
      : `Adjusted ${form.delta} day(s).`);
    setForm({ employeeId: "", leaveTypeId: "", delta: "", reason: "Encashment" });
    all.refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      <Hint>
        Debiting an encashable type raises an approved payout line that the next payroll compute picks up.
        Comp-off credits use positive days. Every adjustment is audited.
      </Hint>
      <SectionCard title="Adjust balance" padding={16}>
        <Row gap={12} wrap align="flex-end">
          <Field label="Employee">
            <Select value={form.employeeId} onChange={(e) => setForm((f) => ({ ...f, employeeId: e.target.value }))}>
              <option value="">Select…</option>
              {employees.map((u) => <option key={u._id} value={u._id}>{fullName(u)}</option>)}
            </Select>
          </Field>
          <Field label="Leave type">
            <Select value={form.leaveTypeId} onChange={(e) => setForm((f) => ({ ...f, leaveTypeId: e.target.value }))}>
              <option value="">Select…</option>
              {(types.data || []).map((ty) => (
                <option key={ty._id} value={ty._id}>{ty.name}{ty.encashable ? " (encashable)" : ""}</option>
              ))}
            </Select>
          </Field>
          <Field label="Days (± delta)"><Input type="number" value={form.delta} onChange={(e) => setForm((f) => ({ ...f, delta: e.target.value }))} placeholder="-5" /></Field>
          <Field label="Reason"><Input value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} /></Field>
          <Button variant="primary" onClick={adjust}>Apply</Button>
        </Row>
        {done && <div style={{ marginTop: 10 }}><Badge tone="positive">{done}</Badge></div>}
      </SectionCard>
      {encashable.length === 0 && !types.loading && (
        <Hint>No leave type is marked encashable yet — flag one in HR Settings ▸ Leave.</Hint>
      )}
    </Stack>
  );
}

const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1).replace(/_/g, " ") : "—");
